import { Flex, Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import { useAccount } from '@feathr/extender/state';

import { useMetaIntegration } from './MetaIntegrationPage.hooks';
import { MetaConnectedBadge, MetaConnectedView, MetaSettingsButton } from './PostConnection';
import { MetaConnectButton, MetaOnboardingView } from './PreConnection';

function MetaIntegrationPage(): JSX.Element {
  const account = useAccount();
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { isLoading, isConnected, hasConnection, error, integration, pages, refetch } =
    useMetaIntegration();

  return (
    <Page
      actions={
        hasConnection && integration ? (
          <MetaSettingsButton integration={integration} onDisconnect={refetch} />
        ) : (
          <MetaConnectButton />
        )
      }
      ChipsComponent={
        hasConnection ? (
          <Flex>
            <MetaConnectedBadge isConnected={isConnected} />
          </Flex>
        ) : undefined
      }
      collapseHeader={false}
      collapseHeaderOnScroll={true}
      description={
        hasConnection
          ? undefined
          : t(
              'Integrate your Facebook Business Manager and Instagram accounts to create, manage, and optimize paid social campaigns alongside your other marketing efforts.',
            )
      }
      includeCurrentPageCrumb={true}
      loading={account.isPending || isLoading}
      title={hasConnection ? t('Meta Integration') : t('Connect Meta for Paid Social Advertising')}
    >
      <Stack gap={theme.spacing[2]}>
        {error && (
          <Alert title={error.title} type={'danger'}>
            {error.message}
          </Alert>
        )}
        {hasConnection ? (
          <MetaConnectedView integration={integration} pages={pages} />
        ) : (
          <MetaOnboardingView />
        )}
      </Stack>
    </Page>
  );
}

export default observer(MetaIntegrationPage);
